/* eslint-disable no-console */
import { AxiosRequestConfig } from 'axios';
import { isArray } from 'lodash-es';
import { HTML_ENCODE_CONFIG } from '../helpers/const';
import { applyTransformToArrayField, getValueByPath, isHtmlEncoded, matchesRoute, setValueByPath } from '../helpers/html-transform';
import { encodeHtml } from '@/shared/helpers';

const showLogging = false;

/**
 * Axios request middleware that HTML-encodes configured fields
 * before PUT / POST requests are sent to the API.
 *
 * - Applies only to routes defined in `HTML_ENCODE_CONFIG`
 * - Encodes only specific fields
 * - Supports single objects, arrays of objects and nested array of objects
 * - Prevents double-encoding by detecting already-encoded values
 *
 * This ensures that user-facing decoded content is safely
 * re-encoded before being sent back to the db.
 */
export const encodeHtmlMiddleware = (request: AxiosRequestConfig): AxiosRequestConfig => {
  const { method = '', url = '', data } = request;
  const config = HTML_ENCODE_CONFIG.find((c) => c.methods.includes(method.toUpperCase()) && matchesRoute(c.path, url));

  if (!config?.fields) return request;

  const requestData = isArray(data) ? data : [data];

  requestData.forEach((item) => {
    config.fields.forEach((field) => {
      if (field.includes('[]')) {
        const didTransform = applyTransformToArrayField(item, field, encodeHtml, true);

        if (showLogging && !didTransform) {
          console.warn(`[HTML Encode Middleware] Field "${field}" found but is not a valid array`);
        }

        if (showLogging && didTransform) {
          console.log(`[HTML Encode Middleware] Field "${field}" was encoded`);
        }

        return;
      }

      const value = getValueByPath(item, field);

      if (typeof value !== 'string') {
        if (showLogging) console.warn(`[HTML Encode Middleware] Skipped field "${field}" — value is not a string`);
        return;
      }
      if (isHtmlEncoded(value)) return;

      const encodedValue = encodeHtml(value);
      setValueByPath(item, field, encodedValue);

      if (showLogging) {
        console.groupCollapsed(`[HTML Encode Middleware] ${field}`);
        console.log('Before:', value);
        console.log('After:', encodedValue);
        console.groupEnd();
      }
    });
  });

  return request;
};
